import type {
  CustomerValueCriterionDefinition,
  CustomerValueCriterionScoringBand,
  CustomerValueCriterionUnitType,
  CustomerValueCriterionValueType,
  CustomerValueModelType
} from "./criterionScoring";
import { resolveCriterionScore } from "./criterionScoring";

export type CustomerValueCriterionSuggestionConfidence = "strong_context" | "partial_context" | "generic_fallback";
export type CustomerValueCriterionSuggestionLanguage = "de" | "en";
export type CustomerLifecycleForDealWin = "existing_customer" | "prospect";

export interface CustomerValueCriterionContextSignal {
  perspectiveLabel: string;
  text: string;
}

export interface CustomerValueCriterionDescriptionSuggestionInput {
  criterionName: string;
  modelType: CustomerValueModelType;
  evaluationType: CustomerValueCriterionDefinition["criterionType"];
  valueType: CustomerValueCriterionValueType;
  unitType: CustomerValueCriterionUnitType;
  scoringDirection: CustomerValueCriterionDefinition["scoringDirection"];
  existingDescription?: string | null;
  language?: CustomerValueCriterionSuggestionLanguage;
  contextSignals?: readonly CustomerValueCriterionContextSignal[];
}

export interface CustomerValueCriterionDescriptionSuggestion {
  suggestedDescription: string;
  sourceSummary: string;
  confidence: CustomerValueCriterionSuggestionConfidence;
  warnings: readonly string[];
}

export interface DealWinCustomerValueCriterionContribution {
  criterionId: string;
  name: string;
  rawValue: unknown;
  unit: CustomerValueCriterionUnitType | string | null;
  points: number | null;
  weight: number;
  weightedContribution: number | null;
  configurationStatus: CustomerValueCriterionDefinition["scoringConfigurationStatus"];
  modelType: CustomerValueModelType;
  modelVersion: number;
  assessedAt: string;
  sourceStatus: "available" | "missing" | "error";
  missingInput: boolean;
  errorCode: string | null;
}

const ATOMIC_SIGNAL_PATTERNS = [
  { key: "revenue_potential", labelDe: "Umsatzpotenzial", labelEn: "revenue potential", patterns: [/umsatz/i, /revenue/i, /arr/i, /mrr/i] },
  { key: "margin_quality", labelDe: "Marge", labelEn: "margin quality", patterns: [/marge/i, /deckungsbeitrag/i, /margin/i, /contribution/i] },
  { key: "strategic_fit", labelDe: "strategische Passung", labelEn: "strategic fit", patterns: [/strateg/i, /fit/i, /zielkund/i, /icp/i] },
  { key: "relationship_quality", labelDe: "Beziehungsqualität", labelEn: "relationship quality", patterns: [/beziehung/i, /vertrauen/i, /relationship/i, /stakeholder/i] },
  { key: "service_effort", labelDe: "Betreuungsaufwand", labelEn: "service effort", patterns: [/aufwand/i, /support/i, /service/i, /komplex/i, /effort/i] },
  { key: "reference_potential", labelDe: "Referenzpotenzial", labelEn: "reference potential", patterns: [/referenz/i, /case study/i, /reference/i, /marken/i, /brand/i] }
] as const;

export function normalizeCustomerValueCriterionName(name: string): string {
  return name
    .normalize("NFKC")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase("de-DE");
}

function compactText(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function unique<T>(items: readonly T[]): T[] {
  return [...new Set(items)];
}

export function detectAtomicCustomerValueSignals(texts: readonly string[]): string[] {
  const haystack = texts.map(compactText).join(" ");
  return ATOMIC_SIGNAL_PATTERNS
    .filter((entry) => entry.patterns.some((pattern) => pattern.test(haystack)))
    .map((entry) => entry.key);
}

export function suggestAtomicCustomerValueCriteria(texts: readonly string[], language: CustomerValueCriterionSuggestionLanguage = "de"): string[] {
  const detected = detectAtomicCustomerValueSignals(texts);
  return detected.map((key) => {
    const entry = ATOMIC_SIGNAL_PATTERNS.find((candidate) => candidate.key === key);
    return language === "en" ? entry?.labelEn ?? key : entry?.labelDe ?? key;
  });
}

export function requiresExplicitHolisticIntentForCombinedCriterion(texts: readonly string[]): boolean {
  return detectAtomicCustomerValueSignals(texts).length > 1;
}

function modelNoun(modelType: CustomerValueModelType, language: CustomerValueCriterionSuggestionLanguage): string {
  if (language === "en") {
    return modelType === "existing_customer_value" ? "realized customer value for existing customers" : "expected new customer value for prospects";
  }
  return modelType === "existing_customer_value" ? "tatsächlichen Customer Value bei Bestandskunden" : "erwarteten New Customer Value bei Prospects";
}

function sourceSummaryFor(input: CustomerValueCriterionDescriptionSuggestionInput, language: CustomerValueCriterionSuggestionLanguage): { summary: string; confidence: CustomerValueCriterionSuggestionConfidence } {
  const contextSignals = input.contextSignals?.filter((signal) => compactText(signal.text).length > 0) ?? [];
  const perspectives = unique(contextSignals.map((signal) => compactText(signal.perspectiveLabel)).filter(Boolean)).slice(0, 4);
  const detectedSignals = suggestAtomicCustomerValueCriteria(contextSignals.map((signal) => signal.text), language).slice(0, 4);
  if (perspectives.length >= 2 && detectedSignals.length >= 1) {
    return {
      confidence: "strong_context",
      summary: language === "en"
        ? `Based on signals from ${perspectives.join(", ")}; relevant themes: ${detectedSignals.join(", ")}.`
        : `Abgeleitet aus Signalen von ${perspectives.join(", ")}; relevante Themen: ${detectedSignals.join(", ")}.`
    };
  }
  if (perspectives.length > 0 || detectedSignals.length > 0) {
    return {
      confidence: "partial_context",
      summary: language === "en"
        ? `Partial context available${perspectives.length ? ` from ${perspectives.join(", ")}` : ""}${detectedSignals.length ? `; themes: ${detectedSignals.join(", ")}` : ""}.`
        : `Teilkontext vorhanden${perspectives.length ? ` aus ${perspectives.join(", ")}` : ""}${detectedSignals.length ? `; Themen: ${detectedSignals.join(", ")}` : ""}.`
    };
  }
  return {
    confidence: "generic_fallback",
    summary: language === "en"
      ? "General Hauffe OS suggestion. No reliable workshop or department signal was available."
      : "Allgemeiner Hauffe-OS-Vorschlag. Es lag kein belastbares Workshop- oder Abteilungssignal vor."
  };
}

export function buildCustomerValueCriterionDescriptionSuggestion(
  input: CustomerValueCriterionDescriptionSuggestionInput
): CustomerValueCriterionDescriptionSuggestion {
  const language = input.language ?? "de";
  const criterionName = compactText(input.criterionName) || (language === "en" ? "this criterion" : "dieses Kriterium");
  const source = sourceSummaryFor(input, language);
  const warnings = [
    ...(input.existingDescription && compactText(input.existingDescription).length > 0 ? ["existing_description_present"] : []),
    ...(source.confidence === "generic_fallback" ? ["generic_context_only"] : [])
  ];
  const directionText = language === "en"
    ? input.scoringDirection === "lower_is_better" ? "Lower values indicate a stronger contribution." : "Higher values indicate a stronger contribution."
    : input.scoringDirection === "lower_is_better" ? "Niedrigere Werte sprechen für einen stärkeren Beitrag." : "Höhere Werte sprechen für einen stärkeren Beitrag.";
  const suggestedDescription = language === "en"
    ? `${criterionName} describes how strongly this dimension contributes to ${modelNoun(input.modelType, language)}. ${directionText} Use a separate criterion whenever revenue, margin, effort or strategic fit need to be assessed independently.`
    : `${criterionName} beschreibt, wie stark diese Dimension zum ${modelNoun(input.modelType, language)} beiträgt. ${directionText} Lege ein eigenes Kriterium an, wenn Umsatz, Marge, Aufwand oder strategische Passung getrennt bewertet werden sollen.`;
  return {
    suggestedDescription,
    sourceSummary: source.summary,
    confidence: source.confidence,
    warnings
  };
}

export function customerValueModelTypeForDealWin(lifecycle: CustomerLifecycleForDealWin): CustomerValueModelType {
  return lifecycle === "existing_customer" ? "existing_customer_value" : "new_customer_value";
}

export function buildDealWinCustomerValueCriterionContribution(input: {
  criterion: CustomerValueCriterionDefinition;
  bands: readonly CustomerValueCriterionScoringBand[];
  rawValue: unknown;
  modelVersion: number;
  assessedAt: string;
}): DealWinCustomerValueCriterionContribution {
  const resolution = resolveCriterionScore(input.criterion, input.bands, input.rawValue);
  const missingInput = resolution.errors.some((error) => error.code === "missing_input");
  const errorCode = resolution.errors[0]?.code ?? null;
  const points = resolution.points ?? null;
  return {
    criterionId: input.criterion.id,
    name: input.criterion.name,
    rawValue: input.rawValue,
    unit: input.criterion.unitType === "custom" ? input.criterion.customUnit ?? "custom" : input.criterion.unitType,
    points,
    weight: input.criterion.weight,
    weightedContribution: points === null ? null : Number(((points / 10) * input.criterion.weight).toFixed(4)),
    configurationStatus: input.criterion.scoringConfigurationStatus,
    modelType: input.criterion.modelType,
    modelVersion: input.modelVersion,
    assessedAt: input.assessedAt,
    sourceStatus: resolution.ok ? "available" : missingInput ? "missing" : "error",
    missingInput,
    errorCode
  };
}
